import { useRef } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight, Zap, Check } from "lucide-react";
import { ASSETS, CTA_URL } from "@/lib/config";
import { trackClick } from "@/lib/tracker";

function MaskedLine({ text, delay = 0, className = "" }) {
    const words = text.split(" ");
    return (
        <span className={className}>
            {words.map((w, i) => (
                <span key={i} className="mask-reveal-line mr-[0.22em]">
                    <motion.span
                        initial={{ y: "110%" }}
                        animate={{ y: 0 }}
                        transition={{
                            duration: 0.9,
                            delay: delay + i * 0.06,
                            ease: [0.2, 0.8, 0.2, 1],
                        }}
                    >
                        {w}
                    </motion.span>
                </span>
            ))}
        </span>
    );
}

export default function Hero() {
    const navigate = useNavigate();
    const tapCountRef = useRef(0);
    const tapTimerRef = useRef(null);

    const onCta = () => {
        trackClick("dapatkan-superzone-hero", "DAPATKAN SUPERZONE");
    };

    const onLogoTap = () => {
        tapCountRef.current += 1;
        if (tapTimerRef.current) clearTimeout(tapTimerRef.current);
        tapTimerRef.current = setTimeout(() => {
            tapCountRef.current = 0;
        }, 2500);
        if (tapCountRef.current >= 5) {
            tapCountRef.current = 0;
            if (tapTimerRef.current) clearTimeout(tapTimerRef.current);
            navigate("/admin");
        }
    };

    return (
        <section
            data-testid="hero-section"
            className="relative min-h-[100svh] w-full overflow-hidden bg-[#0044FE] text-[#FAF9F6]"
        >
            {/* Ambient background — DESKTOP arrangement (red top-left) */}
            <div className="absolute inset-0 bg-grid opacity-70" />
            <div
                className="blob hidden md:block"
                style={{
                    background: "#ED1C24",
                    width: 560,
                    height: 560,
                    top: "-12%",
                    left: "-10%",
                }}
            />
            <div
                className="blob hidden md:block"
                style={{
                    background: "#001b6b",
                    width: 520,
                    height: 520,
                    bottom: "-18%",
                    right: "-8%",
                }}
            />
            <div
                className="blob hidden md:block"
                style={{
                    background: "#ED1C24",
                    width: 300,
                    height: 300,
                    top: "40%",
                    right: "10%",
                    opacity: 0.35,
                }}
            />
            {/* MOBILE gradient arrangement — red at bottom-right + soft red top-right */}
            <div
                className="blob md:hidden"
                style={{
                    background: "#ED1C24",
                    width: 380,
                    height: 380,
                    bottom: "8%",
                    right: "-30%",
                }}
            />
            <div
                className="blob md:hidden"
                style={{
                    background: "#ED1C24",
                    width: 260,
                    height: 260,
                    top: "-8%",
                    right: "-30%",
                    opacity: 0.4,
                }}
            />
            <div
                className="blob md:hidden"
                style={{
                    background: "#001b6b",
                    width: 420,
                    height: 420,
                    top: "40%",
                    left: "-30%",
                }}
            />
            <div className="beam" style={{ top: "18%" }} />
            <div className="beam" style={{ top: "58%", animationDelay: "3s" }} />

            {/* Top header */}
            <header className="relative z-20 flex flex-col md:flex-row md:items-center md:justify-between gap-4 px-5 md:px-10 pt-6 md:pt-8">
                <motion.div
                    initial={{ opacity: 0, y: -12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="flex items-center justify-center md:justify-start gap-3 cursor-pointer select-none"
                    data-testid="ciko-logo"
                    onClick={onLogoTap}
                    role="button"
                    aria-label="CIKO logo"
                >
                    <img
                        src={ASSETS.logo}
                        alt="CIKO"
                        draggable={false}
                        className="h-12 w-12 md:h-14 md:w-14 rounded-full ring-2 ring-[#FAF9F6]/70"
                    />
                    <div className="leading-tight">
                        <div className="font-display text-xl">CIKO</div>
                        <div className="text-[10px] tracking-[0.24em] text-[#FAF9F6]/70">
                            SUPER SCALPER
                        </div>
                    </div>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: -12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.15 }}
                    className="hidden md:flex items-center gap-4 text-xs font-mono-tight text-[#FAF9F6]/80"
                >
                    <span className="flex items-center gap-2">
                        <span className="h-2 w-2 rounded-full bg-[#ED1C24] blink" />
                        LIVE SESSION
                    </span>
                    <span>MYT / GMT+8</span>
                </motion.div>
            </header>

            {/* Hero content */}
            <div className="relative z-10 mx-auto max-w-7xl px-5 md:px-10 pt-12 md:pt-24 pb-24 md:pb-32">
                <h1
                    className="mt-2 md:mt-6 font-display text-center md:text-left leading-[0.9] uppercase text-[13vw] sm:text-[10vw] md:text-[7vw] lg:text-[6.4vw]"
                    data-testid="hero-headline"
                >
                    <span className="block">
                        <MaskedLine text="ENTRY ON POINT" delay={0.15} />
                    </span>
                    {/* Line 2 — MOBILE: stacked, smaller to fit viewport; DESKTOP: side-by-side */}
                    <span className="block mt-2 md:mt-3 text-[10vw] sm:text-[8vw] md:text-[5.4vw] lg:text-[5vw] md:whitespace-nowrap">
                        <span className="mask-reveal-line block md:inline-block">
                            <motion.span
                                initial={{ y: "110%" }}
                                animate={{ y: 0 }}
                                transition={{
                                    duration: 0.9,
                                    delay: 0.55,
                                    ease: [0.2, 0.8, 0.2, 1],
                                }}
                                className="text-outline"
                            >
                                GUNA
                            </motion.span>
                        </span>{" "}
                        <span className="mask-reveal-line align-baseline block md:inline-block mt-2 md:mt-0">
                            <motion.span
                                initial={{ y: "110%" }}
                                animate={{ y: 0 }}
                                transition={{
                                    duration: 0.9,
                                    delay: 0.68,
                                    ease: [0.2, 0.8, 0.2, 1],
                                }}
                                className="superzone-box"
                            >
                                SUPERZONE.
                            </motion.span>
                        </span>
                    </span>
                </h1>

                <div className="mt-8 md:mt-14 grid grid-cols-1 md:grid-cols-12 gap-8 items-start">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1.0, duration: 0.8 }}
                        className="col-span-1 md:col-span-7 text-center md:text-left max-w-2xl mx-auto md:mx-0"
                    >
                        <p
                            className="text-base md:text-lg text-[#FAF9F6]/85 leading-relaxed"
                            data-testid="hero-subheadline"
                        >
                            SUPERZONE expose kawasan{" "}
                            <span className="font-bold text-[#FAF9F6]">
                                Support & Resistance
                            </span>
                            ,{" "}
                            <span className="font-bold text-[#FAF9F6]">
                                Fibonacci Retracement
                            </span>
                            ,{" "}
                            <span className="font-bold text-[#FAF9F6]">
                                Buy &amp; Sell Zone
                            </span>
                            .
                        </p>

                        <ul
                            className="mt-5 md:mt-6 space-y-3 md:space-y-3.5 inline-block text-left"
                            data-testid="hero-benefits-ticks"
                        >
                            {[
                                "Keputusan lagi cepat & tepat.",
                                <>
                                    Akaun tak hangus loss,{" "}
                                    <span className="font-extrabold uppercase tracking-wide text-[#FAF9F6]">
                                        KEBAL MC
                                    </span>
                                    .
                                </>,
                                <>
                                    <span className="font-extrabold uppercase tracking-wide text-[#FAF9F6]">
                                        Proven
                                    </span>{" "}
                                    bantu trader TP konsisten.
                                </>,
                            ].map((line, i) => (
                                <li
                                    key={i}
                                    data-testid={`hero-tick-${i}`}
                                    className="flex items-start gap-3"
                                >
                                    <span
                                        className="mt-0.5 inline-flex items-center justify-center w-6 h-6 md:w-7 md:h-7 shrink-0 bg-[#ED1C24] border-2 border-[#FAF9F6]"
                                        style={{ boxShadow: "3px 3px 0 rgba(0,27,107,0.55)" }}
                                        aria-hidden
                                    >
                                        <Check className="w-3.5 h-3.5 md:w-4 md:h-4 text-[#FAF9F6]" strokeWidth={3.5} />
                                    </span>
                                    <span className="text-sm md:text-base text-[#FAF9F6]/95 leading-snug">
                                        {line}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1.15, duration: 0.8 }}
                        className="col-span-1 md:col-span-5 flex flex-col items-center md:items-end gap-4"
                    >
                        <a
                            href={CTA_URL}
                            onClick={onCta}
                            className="cta-primary text-sm md:text-base"
                            data-testid="hero-cta-button"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <Zap className="w-4 h-4" strokeWidth={3} />
                            DAPATKAN SUPERZONE
                            <ArrowUpRight className="w-5 h-5" strokeWidth={3} />
                        </a>
                    </motion.div>
                </div>

            </div>
        </section>
    );
}
